import { router, usePage } from "@inertiajs/vue3";
import { computed, ref, type Ref } from "vue";

import {
  getSessionSettings,
  setSessionSettings
} from "@ja/portal-query";

import type {
  ActionConfig,
  ListerPageProps,
  PaginationMeta,
  SharedCrudData
} from "../types";
import { bulkAction, destroy } from "../wayfinder/routes";

/**
 * Composable for Inertia Lister state management.
 * Handles search, pagination, sorting, and filtering via Inertia visits.
 */
export function useCrudLister(props: ListerPageProps) {
  const page = usePage();
  const crud = computed(() => (page.props as any).crud as SharedCrudData);

  const loading = ref(false);
  const selected = ref<Set<string | number>>(new Set());
  const searchQuery = ref(props.query ?? "");

  // Fallback to shared data if not in props
  const activeModel = computed(
    () => props.model ?? crud.value?.active?.model ?? ""
  );
  const activeConfig = computed(() => props.config ?? crud.value?.active);

  /** Current sort state */
  const sortState = computed(() => ({
    column: props.sortBy,
    direction: props.sortDir
  }));

  /** Pagination computed from props */
  const pagination: Ref<PaginationMeta> = computed(() => props.pagination);

  /** Build query params for Inertia visit */
  function buildParams(overrides: Record<string, unknown> = {}) {
    const settings = getSessionSettings();

    // Start with all current query parameters to preserve things like 'tab' in editor
    const currentParams = new URL(window.location.href).searchParams;
    const params: Record<string, any> = {};
    currentParams.forEach((value, key) => {
      // Skip old filter params if we're providing new filters in overrides
      // URL params have flat keys like 'filters[field][operator]' that won't be
      // overwritten by the nested filters object, causing stale filters to persist
      if ("filters" in overrides && key.startsWith("filters[")) {
        return;
      }
      // Strip _da when filters are being overridden — the caller should
      // explicitly set _da in overrides to control default filter behavior
      if ("filters" in overrides && key === '_da') {
        return;
      }
      params[key] = value;
    });

    // Build managed params
    const managedParams: Record<string, unknown> = {
      query: searchQuery.value || undefined,
      page: props.pagination.current_page,
      per_page: props.pagination.per_page ?? settings.perPage ?? 20,
      sort_by: props.sortBy || undefined,
      sort_dir: props.sortDir || undefined
    };

    // Handle filters: preserve current filters from props if not overridden
    if (!("filters" in overrides) && props.filters) {
      managedParams.filters = props.filters;
    }

    return {
      ...params,
      ...managedParams,
      ...overrides
    };
  }

  /** Navigate with new params */
  function navigate(params: Record<string, unknown>) {
    if (!activeModel.value) return;

    loading.value = true;
    router.get(window.location.pathname, buildParams(params) as any, {
      preserveState: true,
      preserveScroll: true,
      onFinish: () => {
        loading.value = false;
      }
    });
  }

  /** Handle search - use preserveState: false so the list updates with new results */
  function search(query: string) {
    searchQuery.value = query;
    if (!activeModel.value) return;

    loading.value = true;
    router.get(window.location.pathname, buildParams({ query, page: 1 }) as any, {
      preserveState: false, // Ensure list re-renders with filtered results
      preserveScroll: true,
      onFinish: () => {
        loading.value = false;
      }
    });
  }

  /** Handle page change */
  function goToPage(page: number) {
    navigate({ page });
  }

  /** Handle per page change */
  function setPerPage(perPage: number) {
    void setSessionSettings({ perPage });
    navigate({ per_page: perPage, page: 1 });
  }

  /** Handle sort */
  function sort(column: string) {
    const newDir =
      props.sortBy === column && props.sortDir === "asc" ? "desc" : "asc";
    navigate({ sort_by: column, sort_dir: newDir });
  }

  /** Handle filter change */
  function applyFilter(key: string, value: unknown) {
    navigate({ [key]: value, page: 1 });
  }

  /** Clear all filters */
  function clearFilters() {
    // Pass empty filters object to trigger filter param cleanup in buildParams
    navigate({ filters: {}, query: undefined, page: 1 });
  }

  /** Toggle row selection */
  function toggleSelection(id: string | number) {
    if (selected.value.has(id)) {
      selected.value.delete(id);
    } else {
      selected.value.add(id);
    }
  }

  /** Toggle all rows */
  function toggleAllSelection() {
    if (selected.value.size === props.items.length) {
      selected.value.clear();
    } else {
      props.items.forEach((item) => {
        const id = item.id as string | number;
        selected.value.add(id);
      });
    }
  }

  /** Clear selection */
  function clearSelection() {
    selected.value.clear();
  }

  /** Check if row is selected */
  function isSelected(id: string | number) {
    return selected.value.has(id);
  }

  /** Execute row action */
  function executeRowAction(actionName: string, id: string | number) {
    if (!activeConfig.value) return;

    const actions = activeConfig.value.lister.actions ?? [];
    const action = actions.find((a: ActionConfig) => a.name === actionName);
    if (!action) return;

    if (action.type === "event" && action.eventName) {
      // Dispatch custom event for domain-specific logic
      console.log('[useCrudLister] Dispatching event:', action.eventName, { id, model: activeModel.value });
      window.dispatchEvent(
        new CustomEvent(action.eventName, {
          detail: { id, model: activeModel.value, payload: action.eventPayload }
        })
      );
    } else if (action.route) {
      // For custom routes, replace :id and visit
      const url = action.route.replace(":id", String(id));
      const method = (action.method?.toLowerCase() ?? "post") as any;

      if (method === "get") {
        router.get(url, {}, { preserveScroll: true });
      } else {
        router[method as "post" | "put" | "patch" | "delete"](url, {}, {
          preserveScroll: true
        });
      }
    } else if (action.method === "DELETE") {
      router.delete(destroy({ model: activeModel.value, id }).url, {
        preserveScroll: true
      });
    }
  }

  /** Execute bulk action */
  function executeBulkAction(actionName: string) {
    if (!activeConfig.value) return;

    const bulkActions = activeConfig.value.lister.bulkActions ?? [];
    const action = bulkActions.find((a: ActionConfig) => a.name === actionName);
    if (!action || selected.value.size === 0) return;

    const ids = Array.from(selected.value);
    router.post(
      bulkAction({ model: activeModel.value }).url,
      { action: actionName, ids },
      {
        preserveScroll: true,
        onSuccess: () => clearSelection()
      }
    );
  }

  return {
    // State
    loading,
    selected,
    searchQuery,
    sortState,
    pagination,
    activeModel,
    activeConfig,

    // Actions
    navigate,
    search,
    goToPage,
    setPerPage,
    sort,
    applyFilter,
    clearFilters,

    // Selection
    toggleSelection,
    toggleAllSelection,
    clearSelection,
    isSelected,

    // Row/Bulk actions
    executeRowAction,
    executeBulkAction
  };
}
